import React, { useState } from 'react';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { getImageUrl } from '../../utils/imageUtils';

const BrazoVisualizer = () => {
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);
  const uid = React.useId().replace(/:/g, '');

  const n = 12, gap = 3;
  const xS = 52, xE = 368, yT = 17, yB = 97, yM = 57;
  const totalW = xE - xS;
  const sw = (totalW - gap * (n - 1)) / n;

  const slices = Array.from({ length: n }, (_, i) => {
    const sx = xS + i * (sw + gap);
    return { i, sx, ex: sx + sw, mx: sx + sw / 2 };
  });

  const active = hovered !== null ? hovered : selected;

  return (
    <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '16px', padding: '14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
        <div style={{ width: '32px', height: '32px', background: '#fef3c7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <rect x="1" y="7" width="14" height="2" rx="1" fill="#92400e"/>
            <rect x="7" y="1" width="2" height="14" rx="1" fill="#92400e"/>
          </svg>
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ fontWeight: '700', color: '#2c1810', fontSize: '13px', margin: 0 }}>Brazo Completo</p>
          <p style={{ color: '#8a6a5e', fontSize: '11px', margin: 0 }}>
            {active !== null ? `Porción ${active + 1} de 12 · aprox. 1 persona` : '30 cm · Rinde 10-12 porciones'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px', textAlign: 'center', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: '18px', fontWeight: '700', color: '#2c1810', lineHeight: 1 }}>30</div>
            <div style={{ fontSize: '10px', color: '#8a6a5e' }}>cm</div>
          </div>
          <div style={{ width: '1px', background: '#fde68a' }} />
          <div>
            <div style={{ fontSize: '18px', fontWeight: '700', color: '#c49564', lineHeight: 1 }}>
              {active !== null ? active + 1 : 12}
            </div>
            <div style={{ fontSize: '10px', color: '#8a6a5e' }}>porciones</div>
          </div>
        </div>
      </div>

      <svg
        viewBox="0 0 420 114"
        xmlns="http://www.w3.org/2000/svg"
        style={{ width: '100%', display: 'block', overflow: 'visible' }}
      >
        <defs>
          <linearGradient id={`bg-${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#8B4513"/>
            <stop offset="35%" stopColor="#6b3010"/>
            <stop offset="70%" stopColor="#5a2808"/>
            <stop offset="100%" stopColor="#3d1a05"/>
          </linearGradient>
          <radialGradient id={`end-${uid}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#f5deb3"/>
            <stop offset="14%" stopColor="#c49564"/>
            <stop offset="27%" stopColor="#7a4020"/>
            <stop offset="41%" stopColor="#f0d090"/>
            <stop offset="53%" stopColor="#8b4513"/>
            <stop offset="65%" stopColor="#e8c07a"/>
            <stop offset="75%" stopColor="#6b3010"/>
            <stop offset="86%" stopColor="#d4a870"/>
            <stop offset="100%" stopColor="#3d1a05"/>
          </radialGradient>
          <clipPath id={`clip-${uid}`}>
            <rect x="30" y="17" width="360" height="80"/>
          </clipPath>
        </defs>

        {/* Sombra */}
        <ellipse cx="210" cy="110" rx="192" ry="5" fill="rgba(0,0,0,0.12)"/>

        {/* Cuerpo */}
        <g {...{clipPath: `url(#clip-${uid})`}}>
          <rect x="14" y="17" width="392" height="80" fill={`url(#bg-${uid})`}/>
          <line x1="14" y1="34" x2="406" y2="34" stroke="rgba(255,190,80,0.10)" strokeWidth="1"/>
          <line x1="14" y1="49" x2="406" y2="49" stroke="rgba(0,0,0,0.15)" strokeWidth="1"/>
          <line x1="14" y1="63" x2="406" y2="63" stroke="rgba(255,190,80,0.08)" strokeWidth="1"/>
          <line x1="14" y1="79" x2="406" y2="79" stroke="rgba(0,0,0,0.11)" strokeWidth="1"/>
          <rect x="14" y="17" width="392" height="18" fill="rgba(255,205,100,0.07)"/>
        </g>

        {/* Gaps entre porciones */}
        {slices.slice(1).map(({ i, sx }) => (
          <line key={`gap-${i}`}
            x1={sx - gap / 2} y1={yT + 2}
            x2={sx - gap / 2} y2={yB - 2}
            stroke="rgba(0,0,0,0.28)" strokeWidth={gap}
          />
        ))}

        {/* Highlight de porción activa */}
        {active !== null && (
          <rect
            x={slices[active].sx} y={yT + 1}
            width={sw} height={yB - yT - 2}
            fill="rgba(196,149,100,0.42)"
            style={{ pointerEvents: 'none' }}
          />
        )}

        {/* Números */}
        {slices.map(({ i, mx }) => (
          <text key={`num-${i}`}
            x={mx} y={yM + 4}
            textAnchor="middle"
            fill="rgba(255,215,140,0.5)"
            fontSize="9"
            fontFamily="sans-serif"
            fontWeight="500"
            style={{ pointerEvents: 'none' }}
          >
            {i + 1}
          </text>
        ))}

        {/* Hit areas interactivas */}
        {slices.map(({ i, sx }) => (
          <rect key={`hit-${i}`}
            x={sx} y={yT + 1}
            width={sw} height={yB - yT - 2}
            fill="transparent"
            style={{ cursor: 'pointer' }}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
            onClick={() => setSelected(selected === i ? null : i)}
          />
        ))}

        {/* Extremo izquierdo */}
        <ellipse cx="30" cy="57" rx="17" ry="40" fill={`url(#end-${uid})`}/>
        <ellipse cx="30" cy="57" rx="17" ry="40" fill="none" stroke="#2a1005" strokeWidth="1.3"/>
        <ellipse cx="30" cy="57" rx="13" ry="31" fill="none" stroke="rgba(60,25,5,0.5)" strokeWidth="0.9"/>
        <ellipse cx="30" cy="57" rx="9.5" ry="22" fill="none" stroke="rgba(240,208,144,0.45)" strokeWidth="0.7"/>
        <ellipse cx="30" cy="57" rx="6" ry="14" fill="none" stroke="rgba(60,25,5,0.42)" strokeWidth="0.7"/>
        <ellipse cx="30" cy="57" rx="3" ry="7" fill="none" stroke="rgba(240,208,144,0.35)" strokeWidth="0.6"/>
        <ellipse cx="30" cy="57" rx="1" ry="2.5" fill="rgba(245,220,170,0.5)"/>

        {/* Extremo derecho */}
        <ellipse cx="390" cy="57" rx="17" ry="40" fill={`url(#end-${uid})`}/>
        <ellipse cx="390" cy="57" rx="17" ry="40" fill="none" stroke="#2a1005" strokeWidth="1.3"/>
        <ellipse cx="390" cy="57" rx="13" ry="31" fill="none" stroke="rgba(60,25,5,0.5)" strokeWidth="0.9"/>
        <ellipse cx="390" cy="57" rx="9.5" ry="22" fill="none" stroke="rgba(240,208,144,0.45)" strokeWidth="0.7"/>
        <ellipse cx="390" cy="57" rx="6" ry="14" fill="none" stroke="rgba(60,25,5,0.42)" strokeWidth="0.7"/>
        <ellipse cx="390" cy="57" rx="3" ry="7" fill="none" stroke="rgba(240,208,144,0.35)" strokeWidth="0.6"/>
        <ellipse cx="390" cy="57" rx="1" ry="2.5" fill="rgba(245,220,170,0.5)"/>

        {/* Bordes rectos */}
        <line x1="30" y1="17" x2="390" y2="17" stroke="#2a1005" strokeWidth="1.3"/>
        <line x1="30" y1="97" x2="390" y2="97" stroke="#1a0a05" strokeWidth="1.3"/>
      </svg>
    </div>
  );
};

const SizeSelector = ({ detailProduct, detailSelectedSize, setDetailSelectedSize }) => (
  <div>
    <p style={{ fontSize: '13px', fontWeight: '700', color: '#2c1810', margin: '0 0 10px' }}>Selecciona tu tamaño</p>
    <RadioGroup value={detailSelectedSize} onValueChange={setDetailSelectedSize}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {detailProduct.availableSlice !== false && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '0 16px', height: '72px', width: '100%', boxSizing: 'border-box', border: detailSelectedSize === 'slice' ? '2px solid #2c1810' : '1.5px solid #e8e0d8', borderRadius: '14px', cursor: 'pointer', background: detailSelectedSize === 'slice' ? 'rgba(44,24,16,0.04)' : '#fafaf9' }}>
            <RadioGroupItem value="slice" id="detail-slice" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <p style={{ fontWeight: '600', fontSize: '14px', color: '#2c1810', margin: 0, lineHeight: 1 }}>Porción Individual</p>
              <p style={{ fontSize: '12px', color: '#a08070', margin: '4px 0 0', lineHeight: 1 }}>Perfecto para probar</p>
            </div>
            <span style={{ fontWeight: '700', fontSize: '19px', color: '#c49564', flexShrink: 0, minWidth: '56px', textAlign: 'right' }}>${detailProduct.priceSlice?.toFixed(2)}</span>
          </label>
        )}
        {detailProduct.availableFull !== false && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '0 16px', height: '72px', width: '100%', boxSizing: 'border-box', border: detailSelectedSize === 'full' ? '2px solid #2c1810' : '1.5px solid #e8e0d8', borderRadius: '14px', cursor: 'pointer', background: detailSelectedSize === 'full' ? 'rgba(44,24,16,0.04)' : '#fafaf9' }}>
            <RadioGroupItem value="full" id="detail-full" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <p style={{ fontWeight: '600', fontSize: '14px', color: '#2c1810', margin: 0, lineHeight: 1 }}>Brazo Completo</p>
              <p style={{ fontSize: '12px', color: '#a08070', margin: '4px 0 0', lineHeight: 1 }}>Ideal para compartir · 10-12 porciones</p>
            </div>
            <span style={{ fontWeight: '700', fontSize: '19px', color: '#c49564', flexShrink: 0, minWidth: '56px', textAlign: 'right' }}>${detailProduct.priceFull?.toFixed(2)}</span>
          </label>
        )}
      </div>
    </RadioGroup>
  </div>
);

const QuantityStepper = ({ quantity, setQuantity }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fafaf9', border: '1.5px solid #e8e0d8', borderRadius: '14px', padding: '12px 18px' }}>
    <span style={{ fontSize: '14px', color: '#8a6a5e' }}>Cantidad</span>
    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
      <button
        onClick={() => setQuantity(q => Math.max(1, q - 1))}
        style={{ width: '32px', height: '32px', borderRadius: '50%', border: '2px solid #c49564', background: 'white', color: '#c49564', fontSize: '18px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
      >−</button>
      <span style={{ fontSize: '20px', fontWeight: '700', color: '#2c1810', minWidth: '24px', textAlign: 'center' }}>{quantity}</span>
      <button
        onClick={() => setQuantity(q => q + 1)}
        style={{ width: '32px', height: '32px', borderRadius: '50%', border: '2px solid #c49564', background: 'white', color: '#c49564', fontSize: '18px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
      >+</button>
    </div>
  </div>
);

const ProductModal = ({
  productDetailDialog,
  setProductDetailDialog,
  detailProduct,
  detailProductType,
  detailSelectedSize,
  setDetailSelectedSize,
  detailSelectedOption,
  setDetailSelectedOption,
  detailQuantity,
  setDetailQuantity,
  addToCartFromDetail
}) => {
  const isQuantityProduct = (p) => {
    const n = (p?.name || '').toLowerCase();
    return n.includes('cachito') || n.includes('empanada');
  };

  const showStepper = detailProductType === 'galleta' ||
    detailProductType === 'galleta_especial' ||
    detailProductType === 'desayuno' ||
    detailProductType === 'menu_sal' ||
    (['panaderia', 'bebida'].includes(detailProductType) &&
      (!(detailProduct?.hasOption && detailProduct?.options?.length > 0) || isQuantityProduct(detailProduct)));

  const addButton = (
    <Button
      onClick={addToCartFromDetail}
      disabled={detailProductType === 'brazo' && !detailSelectedSize}
      style={{ width: '100%', padding: '16px', borderRadius: '14px', fontSize: '15px', fontWeight: '600', letterSpacing: '0.3px', border: 'none', cursor: detailProductType === 'brazo' && !detailSelectedSize ? 'not-allowed' : 'pointer', background: (detailProductType === 'brazo' && !detailSelectedSize) ? '#d6cec9' : '#2c1810', color: 'white', boxShadow: (detailProductType === 'brazo' && !detailSelectedSize) ? 'none' : '0 4px 14px rgba(44,24,16,0.25)', transition: 'all 0.2s' }}
    >
      {detailProductType === 'brazo' && !detailSelectedSize ? '⚠️ Selecciona un tamaño primero' : '🛒 Agregar al Pedido'}
    </Button>
  );

  const packPromoSection = detailProductType === 'galleta' && (
    <div style={{ background: '#fdf8f3', border: '1.5px solid #e8e0d8', borderRadius: '14px', padding: '14px 16px' }}>
      <p style={{ fontSize: '12px', color: '#8a6a5e', margin: '0 0 10px', fontWeight: '600' }}>🎁 ¡Ahorra comprando en pack!</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
        {[
          { label: 'Pack de 5 galletas', price: '$6.00' },
          { label: 'Pack de 12 galletas', price: '$14.00' },
          { label: 'Pack personalizado', price: 'desde $1.17/u' },
        ].map(({ label, price }) => (
          <a key={label} href="#galletas-packs" onClick={() => setProductDetailDialog(false)}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white', border: '1px solid #e8e0d8', borderRadius: '10px', padding: '8px 12px', textDecoration: 'none', color: '#2c1810' }}>
            <span style={{ fontSize: '13px' }}>{label}</span>
            <span style={{ fontSize: '13px', fontWeight: '700', color: '#c49564' }}>{price}</span>
          </a>
        ))}
      </div>
    </div>
  );

  const bajoPedidoNote = detailProduct?.bajoPedido && (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '12px', padding: '10px 14px' }}>
      <span style={{ fontSize: '16px', flexShrink: 0 }}>🕒</span>
      <p style={{ fontSize: '12px', color: '#92400e', margin: 0, lineHeight: 1.5 }}>
        <strong>Bajo pedido</strong> — solicitar con mínimo 24 horas de anticipación
      </p>
    </div>
  );

  const optionsSection = detailProduct && (detailProductType === 'panaderia' || detailProductType === 'menu_sal' || detailProductType === 'desayuno' || detailProductType === 'bebida') && detailProduct.hasOption && detailProduct.options?.length > 0 && (
    <div>
      <p style={{ fontSize: '13px', fontWeight: '700', color: '#2c1810', margin: '0 0 10px' }}>Selecciona tu Variante</p>
      <RadioGroup value={detailSelectedOption} onValueChange={setDetailSelectedOption}>
        {detailProduct.options.map((option, index) => (
          <div key={index} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '0 16px', height: '72px', width: '100%', boxSizing: 'border-box', border: detailSelectedOption === option.name ? '2px solid #2c1810' : '1.5px solid #e8e0d8', borderRadius: '14px', cursor: 'pointer', background: detailSelectedOption === option.name ? 'rgba(44,24,16,0.04)' : '#fafaf9' }}>
              <RadioGroupItem value={option.name} id={`opt-${index}`} style={{ flexShrink: 0 }} />
              <span style={{ flex: 1, fontWeight: '600', fontSize: '14px', color: '#2c1810' }}>{option.name}</span>
              <span style={{ fontWeight: '700', fontSize: '19px', color: '#c49564', flexShrink: 0, minWidth: '56px', textAlign: 'right' }}>${option.price?.toFixed(2)}</span>
            </label>
            {option.note && detailSelectedOption === option.name && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '12px', padding: '10px 14px', margin: '6px 0 0 0' }}>
                <span style={{ fontSize: '16px', flexShrink: 0 }}>🕒</span>
                <p style={{ fontSize: '12px', color: '#92400e', margin: 0, lineHeight: 1.5 }}>{option.note}</p>
              </div>
            )}
          </div>
        ))}
      </RadioGroup>
    </div>
  );

  const simplePrice = detailProduct && (detailProductType === 'panaderia' || detailProductType === 'menu_sal' || detailProductType === 'desayuno' || detailProductType === 'bebida' || detailProductType === 'galleta_especial') && (!detailProduct.hasOption || !detailProduct.options?.length) && (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fafaf9', border: '1.5px solid #e8e0d8', borderRadius: '14px', padding: '16px 18px' }}>
      <span style={{ fontSize: '14px', color: '#8a6a5e' }}>Precio</span>
      <span style={{ fontSize: '26px', fontWeight: '700', color: '#c49564' }}>${detailProduct.price?.toFixed(2) || '0.00'}</span>
    </div>
  );

  return (
    <Dialog open={productDetailDialog} onOpenChange={setProductDetailDialog}>
      <DialogContent className="w-[95vw] max-w-3xl p-0 overflow-hidden border-0 shadow-2xl" style={{ borderRadius: '20px', maxHeight: '92vh' }}>
        {detailProduct && (
          <>
            {/* ══ MOBILE ══ */}
            <div className="flex md:hidden flex-col" style={{ maxHeight: '92vh' }}>
              {/* Cabecera fija: imagen + info */}
              <div style={{ flexShrink: 0, position: 'relative' }}>
                <button onClick={() => setProductDetailDialog(false)} style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 10, width: '34px', height: '34px', borderRadius: '50%', background: 'rgba(0,0,0,0.5)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '16px', fontWeight: '700' }}>✕</button>
                <div style={{ background: '#fdf8f3', padding: '16px 16px 10px' }}>
                  <div style={{ borderRadius: '16px', overflow: 'hidden', height: '180px' }}>
                    <img src={getImageUrl(detailProduct.image)} alt={detailProduct.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={(e) => { e.target.src = '/placeholder.jpg'; }}/>
                  </div>
                </div>
                <div style={{ background: '#fdf8f3', padding: '4px 16px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#2c1810', margin: 0, lineHeight: 1.3 }}>{detailProduct.name}</h2>
                  <p style={{ fontSize: '13px', color: '#8a6a5e', lineHeight: '1.7', margin: 0, textAlign: 'justify' }}>{detailProduct.description}</p>
                  {detailProduct.category && (
                    <span style={{ display: 'inline-block', background: '#2c1810', color: 'white', borderRadius: '20px', padding: '3px 12px', fontSize: '11px', letterSpacing: '0.5px', alignSelf: 'flex-start' }}>{detailProduct.category}</span>
                  )}
                </div>
                <div style={{ height: '1px', background: '#f0e8e0', margin: '0 16px' }} />
              </div>
              {/* Contenido scrollable */}
              <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, background: 'white', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {detailProductType === 'brazo' && <BrazoVisualizer />}
                {detailProductType === 'brazo' && <SizeSelector detailProduct={detailProduct} detailSelectedSize={detailSelectedSize} setDetailSelectedSize={setDetailSelectedSize} />}
                {detailProductType === 'galleta' && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fafaf9', border: '1.5px solid #e8e0d8', borderRadius: '14px', padding: '16px 18px' }}>
                    <span style={{ fontSize: '14px', color: '#8a6a5e' }}>Precio por unidad</span>
                    <span style={{ fontSize: '26px', fontWeight: '700', color: '#c49564' }}>${detailProduct.priceUnit?.toFixed(2) || '1.50'}</span>
                  </div>
                )}
                {packPromoSection}
                {bajoPedidoNote}
                {optionsSection}
                {simplePrice}
                {showStepper && <QuantityStepper quantity={detailQuantity} setQuantity={setDetailQuantity} />}
                {addButton}
              </div>
            </div>

            {/* ══ DESKTOP ══ */}
            <div className="hidden md:flex" style={{ maxHeight: '90vh' }}>
              <div style={{ width: '44%', display: 'flex', flexDirection: 'column', background: '#fdf8f3', flexShrink: 0 }}>
                <div style={{ padding: '20px 20px 12px' }}>
                  <div style={{ borderRadius: '16px', overflow: 'hidden', height: '240px' }}>
                    <img src={getImageUrl(detailProduct.image)} alt={detailProduct.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={(e) => { e.target.src = '/placeholder.jpg'; }}/>
                  </div>
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '0 20px 20px' }}>
                  <h2 style={{ fontSize: '19px', fontWeight: '700', color: '#2c1810', margin: '0 0 8px', lineHeight: 1.3 }}>{detailProduct.name}</h2>
                  <p style={{ fontSize: '13px', color: '#8a6a5e', lineHeight: '1.7', margin: 0, textAlign: 'justify' }}>{detailProduct.description}</p>
                  {detailProduct.description?.includes('🔥') && (
                    <div style={{ marginTop: '12px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '12px', padding: '12px' }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <span style={{ fontSize: '16px', flexShrink: 0 }}>🔥</span>
                        <div>
                          <p style={{ fontWeight: '600', color: '#92400e', fontSize: '12px', margin: '0 0 3px' }}>Tip de Oro</p>
                          <p style={{ color: '#b45309', fontSize: '12px', lineHeight: '1.5', margin: 0 }}>{detailProduct.description.split('🔥')[1]?.trim()}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div style={{ marginTop: 'auto', paddingTop: '16px' }}>
                    {detailProduct.category && (
                      <span style={{ display: 'inline-block', background: '#2c1810', color: 'white', borderRadius: '20px', padding: '3px 12px', fontSize: '11px', letterSpacing: '0.5px' }}>{detailProduct.category}</span>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ width: '1px', background: '#f0e8e0', flexShrink: 0 }} />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', padding: '24px 24px 20px', background: 'white', gap: '14px' }}>
                {detailProductType === 'brazo' && <BrazoVisualizer />}
                {detailProductType === 'brazo' && <SizeSelector detailProduct={detailProduct} detailSelectedSize={detailSelectedSize} setDetailSelectedSize={setDetailSelectedSize} />}
                {detailProductType === 'galleta' && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fafaf9', border: '1.5px solid #e8e0d8', borderRadius: '14px', padding: '16px 18px' }}>
                    <span style={{ fontSize: '14px', color: '#8a6a5e' }}>Precio por unidad</span>
                    <span style={{ fontSize: '26px', fontWeight: '700', color: '#c49564' }}>${detailProduct.priceUnit?.toFixed(2) || '1.50'}</span>
                  </div>
                )}
                {packPromoSection}
                {bajoPedidoNote}
                {optionsSection}
                {simplePrice}
                {showStepper && <QuantityStepper quantity={detailQuantity} setQuantity={setDetailQuantity} />}
                <div style={{ flex: 1 }} />
                {addButton}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProductModal;
